import { toast } from "@/hooks/use-toast";
import { SubscriptionsAPI } from "@/services/subscriptions.service";
import { SubscriptionStatusType } from "@/types/subscription.type";
import React, { createContext, useContext } from "react";
import { useQuery } from "react-query";

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatusType | undefined;
  isLoadingSubscriptionStatus: boolean;
  hasSubscription: boolean;
  handleSubscribe: () => void;
  handleManageSubscription: () => void;
  refetchSubscriptionStatus: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
  isAuthenticated: boolean;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({
  children,
  isAuthenticated,
}) => {
  const getSubscriptionStatus = async () => {
    const response = await SubscriptionsAPI.status();
    return response;
  };

  const { data: subscriptionStatus, isLoading: isLoadingSubscriptionStatus, refetch } =
    useQuery<SubscriptionStatusType>({
      queryKey: ["subscription-status"],
      queryFn: getSubscriptionStatus,
      enabled: isAuthenticated,
      refetchOnWindowFocus: false,
    });

  const hasSubscription = Boolean(subscriptionStatus?.has_subscription);

  const handleSubscribe = async () => {
    try {
      const response = await SubscriptionsAPI.checkout();
      if (response && response.url) {
        window.location.href = response.url;
      } else {
        console.error("No checkout URL returned");
        toast({
          variant: "destructive",
          title: "Checkout failed",
          description: "Please try again later.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Checkout failed",
        description: "Please try again later.",
      });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await SubscriptionsAPI.customerPortal();
      if (response && response.url) {
        window.location.href = response.url;
      } else {
        console.error("No customer portal URL returned");
        toast({
          variant: "destructive",
          title: "Unable to access subscription management",
          description: "Please try again later.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Unable to access subscription management",
        description: "Please try again later.",
      });
    }
  };

  const refetchSubscriptionStatus = () => {
    refetch();
  };

  React.useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const status = searchParams.get("subscription");

    if (!status) {
      return;
    }

    const config: Record<
      string,
      {
        variant: "default" | "destructive";
        title: string;
        description: string;
        shouldRefetch: boolean;
      }
    > = {
      created: {
        variant: "default",
        title: "Subscription activated!",
        description:
          "Your subscription is now active. You have access to all premium features.",
        shouldRefetch: true,
      },
      aborted: {
        variant: "destructive",
        title: "Subscription cancelled",
        description:
          "You cancelled the subscription process. You can try again anytime.",
        shouldRefetch: false,
      },
      updated: {
        variant: "default",
        title: "Subscription updated",
        description: "Your subscription has been successfully updated.",
        shouldRefetch: true,
      },
    };

    const toastConfig = config[status];

    if (toastConfig) {
      toast({
        variant: toastConfig.variant,
        title: toastConfig.title,
        description: toastConfig.description,
      });

      if (toastConfig.shouldRefetch) {
        refetch();
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [isAuthenticated, refetch]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionStatus,
        isLoadingSubscriptionStatus,
        hasSubscription,
        handleSubscribe,
        handleManageSubscription,
        refetchSubscriptionStatus,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
